import { type ReactNode, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useAuth } from '../features/auth/auth-context';
import { boardApi, boardKeys } from '../features/board/api';
import { projectApi, projectKeys } from '../features/projects/api';
import { switchOrgAndGo } from '../features/projects/elsewhere';

/**
 * `/work` is gone — APM-PSPACE-g, docs/plan-personal-space.md §3.4.
 *
 * It was the collaborator's own page (APM-GUEST-h) while a `GUEST` had no
 * `project:read`. Since APM-PSPACE-h the default guest role reads the project,
 * so the guest gets the real project page inside the limited shell, and this
 * route only keeps old links alive: notification e-mails and bookmarks carry
 * `/work?task=…`.
 *
 * `?task=X` ⇒ the drawer of task X on its project's Tasks tab. The task is read
 * first because the project page needs `projectId` and the old link never had
 * it. Unreadable or unknown task ⇒ `/projects`, not an error page: the link is
 * old, and the list of one's projects is the right place to land.
 *
 * Unreadable in the session's organization ⇒ ask whether it is in one of my
 * projects **elsewhere** (APM-PSPACE-j): a guest reading a notification e-mail
 * is usually signed in to their personal space, and the task is in organization
 * A. Found ⇒ switch-org, then the same drawer, in A. The lookup answers only for
 * projects the caller is a member of (`GET me/projects/elsewhere/tasks/:id`), so
 * a link to someone else's task still lands on `/projects` and says nothing.
 *
 * `project` and `task` are uuids — a string schema is fine, `JSON.parse` of the
 * router cannot read a uuid (unlike numbers/booleans — trap APM-903b).
 */
const WorkSearch = z.object({
  project: z.uuid().optional().catch(undefined),
  task: z.uuid().optional().catch(undefined),
});

export const Route = createFileRoute('/work')({
  validateSearch: WorkSearch,
  component: Work,
});

function Work(): ReactNode {
  const { t } = useTranslation();
  const { status } = useAuth();
  const navigate = useNavigate();
  const router = useRouter();
  const search = Route.useSearch();
  const taskId = search.task ?? null;

  const task = useQuery({
    queryKey: boardKeys.task(taskId ?? ''),
    queryFn: () => boardApi.task(taskId!),
    enabled: status === 'authenticated' && taskId !== null,
    retry: false,
  });

  // Only once the session's organization has said no — never a second request for the common case.
  const elsewhere = useQuery({
    queryKey: projectKeys.elsewhereTask(taskId ?? ''),
    queryFn: () => projectApi.locateTask(taskId!),
    enabled: status === 'authenticated' && taskId !== null && task.isError,
    retry: false,
  });

  /*
    Leave exactly once: this component stays mounted while the navigation is pending,
    and a re-run of the effect must not start a second one.
  */
  const left = useRef(false);
  useEffect(() => {
    if (left.current || status === 'restoring') return;
    if (status === 'anonymous') {
      left.current = true;
      const next = `${window.location.pathname}${window.location.search}`;
      void navigate({ to: '/login', replace: true, search: { next } });
      return;
    }
    if (taskId !== null) {
      if (task.isPending) return;
      if (task.data) {
        left.current = true;
        void navigate({
          to: '/projects',
          replace: true,
          search: { projectId: task.data.projectId, tab: 'tasks', taskId: task.data.id },
        });
        return;
      }
      if (task.isError) {
        if (elsewhere.isPending) return;
        const location = elsewhere.data?.location;
        if (location) {
          left.current = true;
          const href = router.buildLocation({
            to: '/projects',
            search: { projectId: location.projectId, tab: 'tasks', taskId: location.taskId },
          }).href;
          // The switch failing (membership suspended meanwhile) ⇒ the usual landing.
          switchOrgAndGo(location.orgId, href, { replace: true }).catch(() => {
            void navigate({ to: '/projects', replace: true, search: {} });
          });
          return;
        }
      }
    }
    left.current = true;
    void navigate({
      to: '/projects',
      replace: true,
      search: search.project ? { projectId: search.project } : {},
    });
  }, [
    status,
    taskId,
    task.isPending,
    task.isError,
    task.data,
    elsewhere.isPending,
    elsewhere.data,
    search.project,
    navigate,
    router,
  ]);

  return (
    <div className="flex min-h-dvh items-center justify-center text-sm text-muted-foreground">
      {status === 'restoring' ? t('auth.restoringSession') : t('common.loading')}
    </div>
  );
}
